import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const STATUS_CONFIG = {
  pending:   { label: '—',                    color: 'text-gray-400 bg-gray-50 border-gray-200',    dot: 'bg-gray-300' },
  running:   { label: 'Probando...',           color: 'text-blue-700 bg-blue-50 border-blue-200',    dot: 'bg-blue-400 animate-pulse' },
  ok:        { label: 'OK',                    color: 'text-green-700 bg-green-50 border-green-200', dot: 'bg-green-500' },
  error:     { label: 'Error',                 color: 'text-red-700 bg-red-50 border-red-200',       dot: 'bg-red-500' },
  loading:   { label: 'Cargando demasiado',    color: 'text-yellow-700 bg-yellow-50 border-yellow-200', dot: 'bg-yellow-400' },
  spinner:   { label: 'Spinner infinito',      color: 'text-orange-700 bg-orange-50 border-orange-200', dot: 'bg-orange-400' },
  noresponse:{ label: 'Sin respuesta',         color: 'text-purple-700 bg-purple-50 border-purple-200', dot: 'bg-purple-400' },
};

const TIMEOUT_MS = 6000;
const ADMIN_LOGIN_TIMEOUT_AUTH_MS = 8000;
const ADMIN_LOGIN_TIMEOUT_PROFILE_MS = 5000;
const ADMIN_LOGIN_TIMEOUT_REDIRECT_MS = 2000;
const RAW_AUTH_TIMEOUT_MS = 12000;

// ── Raw Auth Request Test ───────────────────────────────────────────────────
// Minimal test: only signInWithPassword, no profile fetch, no redirect.
// Shows REQUEST_SENT, RESPONSE_OK, RESPONSE_ERROR, RESPONSE_TIMEOUT + elapsed_ms.
async function runRawAuthTest(onPhase) {
  const phases = [];
  const log = (phase, detail = '') => {
    phases?.push({ phase, detail, time: new Date()?.toLocaleTimeString('es-MX') });
    onPhase?.([...phases]);
  };

  const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
  const supabaseKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;
  if (!supabaseUrl || !supabaseKey) {
    log('RESPONSE_ERROR', 'VITE_SUPABASE_URL o VITE_SUPABASE_ANON_KEY no definidas');
    return { status: 'error', message: 'Supabase auth config missing', phases, lastPhase: 'RESPONSE_ERROR' };
  }

  log('REQUEST_SENT', `URL: ${supabaseUrl?.substring(0, 40)}...`);
  const start = Date.now();

  // Diagnostic timeout signal — does NOT cancel the real promise
  let timeoutId = null;
  const timeoutSignal = new Promise((resolve) => {
    timeoutId = setTimeout(() => resolve('__TIMEOUT__'), RAW_AUTH_TIMEOUT_MS);
  });

  let authResult = null;
  let authException = null;

  // Fire the real call — no wrapper, no race on the call itself
  const authCall = supabase?.auth?.signInWithPassword({
    email: 'admin@gearcontrol.com',
    password: 'admin123',
  })?.then((r) => r)?.catch((e) => { authException = e; return null; });

  // Race only to detect timeout for display purposes
  const winner = await Promise.race([authCall, timeoutSignal]);
  clearTimeout(timeoutId);

  const elapsed = Date.now() - start;

  if (winner === '__TIMEOUT__') {
    log('RESPONSE_TIMEOUT', `AUTH endpoint no respondió en ${RAW_AUTH_TIMEOUT_MS / 1000}s (${elapsed}ms)`);
    // Still wait for the real call to settle in background (non-blocking)
    authCall?.then(() => supabase?.auth?.signOut?.()?.catch(() => {}));
    return {
      status: 'timeout',
      message: `AUTH endpoint no respondió — timeout diagnóstico ${RAW_AUTH_TIMEOUT_MS / 1000}s (${elapsed}ms)`,
      phases,
      lastPhase: 'RESPONSE_TIMEOUT',
    };
  }

  authResult = winner;

  if (authException) {
    log('RESPONSE_ERROR', `${authException?.message} (${elapsed}ms)`);
    return { status: 'error', message: `signInWithPassword lanzó excepción: ${authException?.message} (${elapsed}ms)`, phases, lastPhase: 'RESPONSE_ERROR' };
  }

  if (authResult?.error) {
    log('RESPONSE_ERROR', `${authResult?.error?.message} (${elapsed}ms)`);
    supabase?.auth?.signOut()?.catch(() => {});
    return {
      status: 'error',
      message: `Auth respondió con error en ${elapsed}ms: ${authResult?.error?.message}`,
      phases,
      lastPhase: 'RESPONSE_ERROR',
    };
  }

  if (!authResult?.data?.user) {
    log('RESPONSE_ERROR', `Sin user en respuesta (${elapsed}ms)`);
    supabase?.auth?.signOut()?.catch(() => {});
    return { status: 'error', message: `Auth OK pero sin user (${elapsed}ms)`, phases, lastPhase: 'RESPONSE_ERROR' };
  }

  log('RESPONSE_OK', `user_id=${authResult?.data?.user?.id} (${elapsed}ms)`);
  supabase?.auth?.signOut()?.catch(() => {});

  return {
    status: 'ok',
    message: `Raw Auth OK — signInWithPassword respondió en ${elapsed}ms`,
    phases,
    lastPhase: 'RESPONSE_OK',
  };
}

// ── Auth Health Check ───────────────────────────────────────────────────────
async function runAuthHealthCheck(onPhase) {
  const phases = [];
  const log = (phase, detail = '') => {
    phases?.push({ phase, detail, time: new Date()?.toLocaleTimeString('es-MX') });
    onPhase?.([...phases]);
  };

  // 1. Supabase client initialized?
  log('SUPABASE_CLIENT_READY', supabase ? 'cliente disponible' : 'cliente NULL');
  if (!supabase) {
    return { status: 'error', message: 'Supabase client is null/undefined', phases, lastPhase: 'SUPABASE_CLIENT_READY' };
  }

  // 2. Auth config present?
  const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
  const supabaseKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;
  if (!supabaseUrl || !supabaseKey) {
    log('AUTH_CONFIG_MISSING', 'VITE_SUPABASE_URL o VITE_SUPABASE_ANON_KEY no definidas');
    return { status: 'error', message: 'Supabase auth config missing', phases, lastPhase: 'AUTH_CONFIG_MISSING' };
  }
  log('AUTH_CONFIG_OK', `URL: ${supabaseUrl?.substring(0, 30)}...`);

  // 3. Minimal login call with 12s diagnostic timeout
  log('AUTH_REQUEST_START');
  const authStart = Date.now();

  // Diagnostic timeout signal — does NOT cancel the real promise
  let healthTimeoutId = null;
  const healthTimeoutSignal = new Promise((resolve) => {
    healthTimeoutId = setTimeout(() => resolve('__TIMEOUT__'), RAW_AUTH_TIMEOUT_MS);
  });

  let authResult = null;
  let authException = null;

  const authCall = supabase?.auth?.signInWithPassword({
    email: 'admin@gearcontrol.com',
    password: 'admin123',
  })?.then((r) => r)?.catch((e) => { authException = e; return null; });

  const winner = await Promise.race([authCall, healthTimeoutSignal]);
  clearTimeout(healthTimeoutId);

  const authElapsed = Date.now() - authStart;

  if (winner === '__TIMEOUT__') {
    log('AUTH_REQUEST_TIMEOUT', `AUTH endpoint no respondió en ${RAW_AUTH_TIMEOUT_MS / 1000}s (${authElapsed}ms)`);
    authCall?.then(() => supabase?.auth?.signOut?.()?.catch(() => {}));
    return {
      status: 'error',
      message: `AUTH endpoint no respondió — timeout diagnóstico ${RAW_AUTH_TIMEOUT_MS / 1000}s (${authElapsed}ms)`,
      phases,
      lastPhase: 'AUTH_REQUEST_TIMEOUT',
    };
  }

  authResult = winner;

  if (authException) {
    log('AUTH_REQUEST_ERROR', `${authException?.message} (${authElapsed}ms)`);
    return { status: 'error', message: `signInWithPassword lanzó excepción: ${authException?.message} (${authElapsed}ms)`, phases, lastPhase: 'AUTH_REQUEST_ERROR' };
  }

  const authData = authResult?.data;
  const authError = authResult?.error;

  if (authError) {
    log('AUTH_REQUEST_ERROR', `${authError?.message} (${authElapsed}ms)`);
    supabase?.auth?.signOut()?.catch(() => {});
    return {
      status: 'error',
      message: `Auth respondió con error en ${authElapsed}ms: ${authError?.message}`,
      phases,
      lastPhase: 'AUTH_REQUEST_ERROR',
    };
  }

  if (!authData?.user) {
    log('AUTH_REQUEST_ERROR', `Sin user en respuesta (${authElapsed}ms)`);
    supabase?.auth?.signOut()?.catch(() => {});
    return { status: 'error', message: `Auth OK pero sin user (${authElapsed}ms)`, phases, lastPhase: 'AUTH_REQUEST_ERROR' };
  }

  log('AUTH_REQUEST_SUCCESS', `user_id=${authData?.user?.id} (${authElapsed}ms)`);
  supabase?.auth?.signOut()?.catch(() => {});

  return {
    status: 'ok',
    message: `Auth Health Check OK — signInWithPassword respondió en ${authElapsed}ms`,
    phases,
    lastPhase: 'AUTH_REQUEST_SUCCESS',
  };
}

// ── Admin login phase tracker ───────────────────────────────────────────────
const ADMIN_LOGIN_PHASES = [
  'START',
  'AUTH_REQUEST',
  'AUTH_RESPONSE_OK',
  'AUTH_RESPONSE_ERROR',
  'PROFILE_FETCH_START',
  'PROFILE_FETCH_OK',
  'REDIRECT_START',
  'REDIRECT_OK',
  'TIMEOUT',
];

async function runAdminLoginDiagnostic(onPhase) {
  const phases = [];
  const log = (phase, detail = '') => {
    phases?.push({ phase, detail, time: new Date()?.toLocaleTimeString('es-MX') });
    onPhase?.([...phases]);
  };

  log('START');

  // ── Step 1: Auth request with 12s diagnostic timeout ─────────────────────
  log('AUTH_REQUEST');
  const authStart = Date.now();

  let diagTimeoutId = null;
  const diagTimeoutSignal = new Promise((resolve) => {
    diagTimeoutId = setTimeout(() => resolve('__TIMEOUT__'), RAW_AUTH_TIMEOUT_MS);
  });

  let authResult = null;
  let authException = null;

  const authCall = supabase?.auth?.signInWithPassword({
    email: 'admin@gearcontrol.com',
    password: 'admin123',
  })?.then((r) => r)?.catch((e) => { authException = e; return null; });

  const winner = await Promise.race([authCall, diagTimeoutSignal]);
  clearTimeout(diagTimeoutId);

  const authElapsed = Date.now() - authStart;

  if (winner === '__TIMEOUT__') {
    log('TIMEOUT', `AUTH endpoint no respondió en ${RAW_AUTH_TIMEOUT_MS / 1000}s (${authElapsed}ms)`);
    authCall?.then(() => supabase?.auth?.signOut?.()?.catch(() => {}));
    return {
      status: 'loading',
      message: `AUTH endpoint no respondió — timeout diagnóstico ${RAW_AUTH_TIMEOUT_MS / 1000}s (${authElapsed}ms)`,
      phases,
      lastPhase: 'TIMEOUT',
    };
  }

  authResult = winner;

  if (authException) {
    log('AUTH_RESPONSE_ERROR', `${authException?.message} (${authElapsed}ms)`);
    return { status: 'error', message: `AUTH_REQUEST lanzó excepción: ${authException?.message} (${authElapsed}ms)`, phases, lastPhase: 'AUTH_RESPONSE_ERROR' };
  }

  const authData = authResult?.data;
  const authError = authResult?.error;

  if (authError) {
    log('AUTH_RESPONSE_ERROR', `${authError?.message} (${authElapsed}ms)`);
    return { status: 'error', message: `Auth error (${authElapsed}ms): ${authError?.message}`, phases, lastPhase: 'AUTH_RESPONSE_ERROR' };
  }

  if (!authData?.user) {
    log('AUTH_RESPONSE_ERROR', `No user returned (${authElapsed}ms)`);
    return { status: 'error', message: `Auth OK pero sin user en respuesta (${authElapsed}ms)`, phases, lastPhase: 'AUTH_RESPONSE_ERROR' };
  }

  log('AUTH_RESPONSE_OK', `user_id=${authData?.user?.id} (${authElapsed}ms)`);

  // ── Step 2: Profile fetch ─────────────────────────────────────────────────
  log('PROFILE_FETCH_START');
  let profileData = null;
  let profileError = null;
  const profileStart = Date.now();

  try {
    const profileResult = await Promise.race([
      supabase?.from('user_profiles')?.select('id, role, company_id')?.eq('id', authData?.user?.id)?.maybeSingle(),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('PROFILE_TIMEOUT')), ADMIN_LOGIN_TIMEOUT_PROFILE_MS)
      ),
    ]);
    profileData = profileResult?.data;
    profileError = profileResult?.error;
  } catch (err) {
    const profileElapsed = Date.now() - profileStart;
    if (err?.message === 'PROFILE_TIMEOUT') {
      log('TIMEOUT', `Profile fetch no respondió en ${ADMIN_LOGIN_TIMEOUT_PROFILE_MS / 1000}s (${profileElapsed}ms)`);
      supabase?.auth?.signOut()?.catch(() => {});
      return {
        status: 'loading',
        message: `Auth OK ✓ (${authElapsed}ms) — TIMEOUT en PROFILE_FETCH (>${ADMIN_LOGIN_TIMEOUT_PROFILE_MS / 1000}s)`,
        phases,
        lastPhase: 'TIMEOUT',
      };
    }
    log('PROFILE_FETCH_OK', `(error ignorado: ${err?.message})`);
  }

  const profileElapsed = Date.now() - profileStart;
  if (profileError) {
    log('PROFILE_FETCH_OK', `(error no bloqueante: ${profileError?.message}) (${profileElapsed}ms)`);
  } else {
    log('PROFILE_FETCH_OK', `role=${profileData?.role ?? 'null'}, company_id=${profileData?.company_id ?? 'null'} (${profileElapsed}ms)`);
  }

  // ── Step 3: Redirect simulation ───────────────────────────────────────────
  log('REDIRECT_START');
  await new Promise((resolve) => setTimeout(resolve, 100));
  log('REDIRECT_OK', '→ /workshop-dashboard');

  supabase?.auth?.signOut()?.catch(() => {});

  return {
    status: 'ok',
    message: `Todas las fases OK: Auth ✓ (${authElapsed}ms) Profile(${profileData?.role ?? '?'}) ✓ Redirect ✓`,
    phases,
    lastPhase: 'REDIRECT_OK',
  };
}

// ── Action definitions ──────────────────────────────────────────────────────
const MODULES = [
  {
    id: 'signin',
    name: 'Sign In Interno',
    icon: '🔐',
    actions: [
      { id: 'signin_admin',      label: 'Login admin',      description: 'Verificar que el endpoint de auth responde para rol admin' },
      { id: 'signin_recepcion',  label: 'Login recepción',  description: 'Verificar que el endpoint de auth responde para rol recepción' },
      { id: 'signin_taller',     label: 'Login taller',     description: 'Verificar que el endpoint de auth responde para rol taller' },
      { id: 'signin_ventas',     label: 'Login ventas',     description: 'Verificar que el endpoint de auth responde para rol ventas' },
    ],
  },
  {
    id: 'portal',
    name: 'Portal Cliente',
    icon: '🌐',
    actions: [
      { id: 'portal_login',      label: 'Login portal',         description: 'Ruta /portal/login accesible y renderiza formulario' },
      { id: 'portal_acceso',     label: 'Solicitar acceso',     description: 'Ruta /portal/login con flujo de solicitud de acceso' },
      { id: 'portal_vehiculos',  label: 'Ver vehículos',        description: 'Tabla portal_vehicles consultable sin error' },
      { id: 'portal_historial',  label: 'Ver historial',        description: 'Tabla vehicle_history consultable sin error' },
      { id: 'portal_citas',      label: 'Ver citas',            description: 'Tabla calendar_events consultable sin error' },
    ],
  },
  {
    id: 'recepcion',
    name: 'Recepción de Unidad',
    icon: '🚗',
    actions: [
      { id: 'recep_buscar',      label: 'Buscar cliente',       description: 'Query a tabla clients con filtro de texto' },
      { id: 'recep_seleccionar', label: 'Seleccionar cliente',  description: 'Obtener cliente por ID sin error' },
      { id: 'recep_vehiculo',    label: 'Seleccionar vehículo', description: 'Query a tabla vehicles filtrado por client_id' },
      { id: 'recep_guardar',     label: 'Guardar y continuar',  description: 'Insert de prueba en receptions (modo test, rollback)' },
    ],
  },
  {
    id: 'solicitudes',
    name: 'Solicitudes',
    icon: '📋',
    actions: [
      { id: 'sol_detalle',       label: 'Abrir detalle',        description: 'Query a service_requests con limit 1' },
      { id: 'sol_reagendar',     label: 'Reagendar',            description: 'Verificar columna scheduled_date en service_requests' },
      { id: 'sol_cancelar',      label: 'Cancelar',             description: 'Verificar columna status en service_requests' },
      { id: 'sol_mensaje',       label: 'Enviar mensaje',       description: 'Query a solicitud_messages sin error' },
    ],
  },
  {
    id: 'cotizacion',
    name: 'Cotización',
    icon: '💰',
    actions: [
      { id: 'cot_crear',         label: 'Crear cotización',     description: 'Verificar acceso a tabla quotations' },
      { id: 'cot_borrador',      label: 'Guardar borrador',     description: 'Verificar status "guardada" disponible en quotations' },
    ],
  },
  {
    id: 'citas',
    name: 'Citas',
    icon: '📅',
    actions: [
      { id: 'citas_crear',       label: 'Crear cita',           description: 'Query a tabla calendar_events sin error' },
      { id: 'citas_horario',     label: 'Validar horario',      description: 'Verificar appointmentConfig disponible' },
    ],
  },
  {
    id: 'crm',
    name: 'CRM',
    icon: '👥',
    actions: [
      { id: 'crm_cliente',       label: 'Abrir cliente',        description: 'Query a tabla crm_contacts con limit 1' },
      { id: 'crm_interaccion',   label: 'Registrar interacción', description: 'Verificar acceso a tabla crm_activities' },
    ],
  },
];

// ── Individual action runners ───────────────────────────────────────────────
async function runAction(actionId, companyId) {
  const withTimeout = (promise) =>
    Promise.race([
      promise,
      new Promise((_, reject) => setTimeout(() => reject(new Error('TIMEOUT')), TIMEOUT_MS)),
    ]);

  try {
    switch (actionId) {
      // Sign In — just verify Supabase auth endpoint is reachable
      case 'signin_admin': {
        // Quick check: just verify user_profiles has an admin row (non-destructive)
        const { data, error } = await withTimeout(
          supabase?.from('user_profiles')?.select('id, role')?.eq('role', 'admin')?.limit(1)
        );
        if (error) return { status: 'error', message: error?.message };
        const count = data?.length ?? 0;
        return { status: 'ok', message: `${count} usuario(s) con rol "admin" encontrado(s). Usa "Probar solo login admin" para diagnóstico completo.` };
      }

      // Portal
      case 'portal_login': {
        const resp = await withTimeout(fetch(window.location?.origin + '/portal/login', { method: 'HEAD' }));
        return resp?.ok ? { status: 'ok', message: 'Ruta /portal/login responde 200' } : { status: 'error', message: `HTTP ${resp?.status}` };
      }
      case 'portal_acceso': {
        const { data, error } = await withTimeout(
          supabase?.from('portal_access_requests')?.select('id')?.limit(1)
        );
        if (error) return { status: 'error', message: error?.message };
        return { status: 'ok', message: `Tabla portal_access_requests accesible (${data?.length ?? 0} registros)` };
      }
      case 'portal_vehiculos': {
        const { data, error } = await withTimeout(
          supabase?.from('portal_vehicles')?.select('id')?.limit(1)
        );
        if (error) return { status: 'error', message: error?.message };
        return { status: 'ok', message: `Tabla portal_vehicles accesible (${data?.length ?? 0} registros)` };
      }
      case 'portal_historial': {
        const { data, error } = await withTimeout(
          supabase?.from('vehicle_history')?.select('id')?.limit(1)
        );
        if (error) return { status: 'error', message: error?.message };
        return { status: 'ok', message: `Tabla vehicle_history accesible (${data?.length ?? 0} registros)` };
      }
      case 'portal_citas': {
        const { data, error } = await withTimeout(
          supabase?.from('calendar_events')?.select('id')?.limit(1)
        );
        if (error) return { status: 'error', message: error?.message };
        return { status: 'ok', message: `Tabla calendar_events accesible (${data?.length ?? 0} registros)` };
      }

      // Recepción
      case 'recep_buscar': {
        const { data, error } = await withTimeout(
          supabase?.from('clients')?.select('id, name')?.ilike('name', '%a%')?.limit(3)
        );
        if (error) return { status: 'error', message: error?.message };
        return { status: 'ok', message: `Búsqueda de clientes OK (${data?.length ?? 0} resultados)` };
      }
      case 'recep_seleccionar': {
        const { data, error } = await withTimeout(
          supabase?.from('clients')?.select('id, name')?.limit(1)
        );
        if (error) return { status: 'error', message: error?.message };
        if (!data?.length) return { status: 'ok', message: 'Sin clientes aún, pero query OK' };
        return { status: 'ok', message: `Cliente obtenido: ${data?.[0]?.name ?? data?.[0]?.id}` };
      }
      case 'recep_vehiculo': {
        const { data: clients } = await withTimeout(supabase?.from('clients')?.select('id')?.limit(1));
        if (!clients?.length) return { status: 'ok', message: 'Sin clientes para probar, query OK' };
        const { data, error } = await withTimeout(
          supabase?.from('vehicles')?.select('id, brand, model')?.eq('client_id', clients?.[0]?.id)?.limit(3)
        );
        if (error) return { status: 'error', message: error?.message };
        return { status: 'ok', message: `Vehículos por cliente OK (${data?.length ?? 0} encontrados)` };
      }
      case 'recep_guardar': {
        // Dry-run: verify table is accessible without inserting
        const { error } = await withTimeout(
          supabase?.from('receptions')?.select('id')?.limit(1)
        );
        if (error) return { status: 'error', message: error?.message };
        return { status: 'ok', message: 'Tabla receptions accesible (sin insertar datos reales)' };
      }

      // Solicitudes
      case 'sol_detalle': {
        const { data, error } = await withTimeout(
          supabase?.from('service_requests')?.select('id, status')?.limit(1)
        );
        if (error) return { status: 'error', message: error?.message };
        return { status: 'ok', message: `service_requests accesible (${data?.length ?? 0} registros)` };
      }
      case 'sol_reagendar': {
        const { data, error } = await withTimeout(
          supabase?.from('service_requests')?.select('id, scheduled_date')?.limit(1)
        );
        if (error) return { status: 'error', message: error?.message };
        return { status: 'ok', message: 'Columna scheduled_date disponible en service_requests' };
      }
      case 'sol_cancelar': {
        const { data, error } = await withTimeout(
          supabase?.from('service_requests')?.select('id, status')?.limit(1)
        );
        if (error) return { status: 'error', message: error?.message };
        return { status: 'ok', message: 'Columna status disponible en service_requests' };
      }
      case 'sol_mensaje': {
        const { data, error } = await withTimeout(
          supabase?.from('solicitud_messages')?.select('id')?.limit(1)
        );
        if (error) return { status: 'error', message: error?.message };
        return { status: 'ok', message: `solicitud_messages accesible (${data?.length ?? 0} registros)` };
      }

      // Cotización
      case 'cot_crear': {
        const { data, error } = await withTimeout(
          supabase?.from('quotations')?.select('id, status')?.limit(1)
        );
        if (error) return { status: 'error', message: error?.message };
        return { status: 'ok', message: `Tabla quotations accesible (${data?.length ?? 0} registros)` };
      }
      case 'cot_borrador': {
        const { data, error } = await withTimeout(
          supabase?.from('quotations')?.select('id, status')?.eq('status', 'guardada')?.limit(1)
        );
        if (error) return { status: 'error', message: error?.message };
        return { status: 'ok', message: `Status "guardada" disponible (${data?.length ?? 0} registros)` };
      }

      // Citas
      case 'citas_crear': {
        const { data, error } = await withTimeout(
          supabase?.from('calendar_events')?.select('id, title')?.limit(1)
        );
        if (error) return { status: 'error', message: error?.message };
        return { status: 'ok', message: `Tabla calendar_events accesible (${data?.length ?? 0} registros)` };
      }
      case 'citas_horario': {
        try {
          const mod = await import('../../utils/appointmentConfig.js');
          const keys = Object.keys(mod ?? {});
          return { status: 'ok', message: `appointmentConfig cargado (${keys?.length} exports: ${keys?.join(', ')})` };
        } catch (e) {
          return { status: 'error', message: `No se pudo cargar appointmentConfig: ${e?.message}` };
        }
      }

      // CRM
      case 'crm_cliente': {
        const { data, error } = await withTimeout(
          supabase?.from('crm_contacts')?.select('id, name')?.limit(1)
        );
        if (error) return { status: 'error', message: error?.message };
        return { status: 'ok', message: `crm_contacts accesible (${data?.length ?? 0} registros)` };
      }
      case 'crm_interaccion': {
        const { data, error } = await withTimeout(
          supabase?.from('crm_activities')?.select('id')?.limit(1)
        );
        if (error) return { status: 'error', message: error?.message };
        return { status: 'ok', message: `crm_activities accesible (${data?.length ?? 0} registros)` };
      }

      default:
        return { status: 'error', message: `Acción desconocida: ${actionId}` };
    }
  } catch (err) {
    if (err?.message === 'TIMEOUT') {
      return { status: 'loading', message: `Timeout después de ${TIMEOUT_MS / 1000}s` };
    }
    return { status: 'error', message: err?.message ?? 'Error desconocido' };
  }
}

// ── Component ───────────────────────────────────────────────────────────────
export default function QAActions() {
  const { userProfile, lastBootstrapPhase } = useAuth();
  const navigate = useNavigate();

  // Build initial state: { actionId: { status, message, testedAt } }
  const buildInitial = () => {
    const s = {};
    MODULES?.forEach((m) => m?.actions?.forEach((a) => { s[a.id] = { status: 'pending', message: '', testedAt: null }; }));
    return s;
  };

  const [results, setResults] = useState(buildInitial);
  const [running, setRunning] = useState(false);
  const [reportTime, setReportTime] = useState(null);

  // ── Admin login solo test state ───────────────────────────────────────────
  const [adminLoginRunning, setAdminLoginRunning] = useState(false);
  const [adminLoginPhases, setAdminLoginPhases] = useState([]);
  const [adminLoginResult, setAdminLoginResult] = useState(null);

  // ── Auth Health Check state ───────────────────────────────────────────────
  const [healthRunning, setHealthRunning] = useState(false);
  const [healthPhases, setHealthPhases] = useState([]);
  const [healthResult, setHealthResult] = useState(null);

  // ── Raw Auth Request Test state ───────────────────────────────────────────
  const [rawAuthRunning, setRawAuthRunning] = useState(false);
  const [rawAuthPhases, setRawAuthPhases] = useState([]);
  const [rawAuthResult, setRawAuthResult] = useState(null);

  const runRawAuth = useCallback(async () => {
    setRawAuthRunning(true);
    setRawAuthPhases([]);
    setRawAuthResult(null);
    try {
      const result = await runRawAuthTest((phases) => setRawAuthPhases([...phases]));
      setRawAuthResult(result);
    } catch (err) {
      setRawAuthResult({ status: 'error', message: err?.message ?? 'Error inesperado', phases: [], lastPhase: 'RESPONSE_ERROR' });
    } finally {
      setRawAuthRunning(false);
    }
  }, []);

  const runHealthCheck = useCallback(async () => {
    setHealthRunning(true);
    setHealthPhases([]);
    setHealthResult(null);
    try {
      const result = await runAuthHealthCheck((phases) => setHealthPhases([...phases]));
      setHealthResult(result);
    } catch (err) {
      setHealthResult({ status: 'error', message: err?.message ?? 'Error inesperado', phases: [], lastPhase: 'ERROR' });
    } finally {
      setHealthRunning(false);
    }
  }, []);

  const runAdminLoginSolo = useCallback(async () => {
    setAdminLoginRunning(true);
    setAdminLoginPhases([]);
    setAdminLoginResult(null);
    try {
      const result = await runAdminLoginDiagnostic((phases) => setAdminLoginPhases([...phases]));
      setAdminLoginResult(result);
    } catch (err) {
      setAdminLoginResult({ status: 'error', message: err?.message ?? 'Error inesperado', phases: adminLoginPhases, lastPhase: 'ERROR' });
    } finally {
      setAdminLoginRunning(false);
    }
  }, []);

  const setActionResult = useCallback((actionId, result) => {
    setResults((prev) => ({
      ...prev,
      [actionId]: { status: result?.status, message: result?.message, testedAt: new Date()?.toLocaleTimeString('es-MX') },
    }));
  }, []);

  const runAll = useCallback(async () => {
    setRunning(true);
    setReportTime(null);
    // Mark all as running
    const running = {};
    MODULES?.forEach((m) => m?.actions?.forEach((a) => { running[a.id] = { status: 'running', message: '', testedAt: null }; }));
    setResults(running);

    const companyId = userProfile?.company_id;
    for (const mod of MODULES) {
      for (const action of mod?.actions) {
        const result = await runAction(action?.id, companyId);
        setActionResult(action?.id, result);
      }
    }
    setRunning(false);
    setReportTime(new Date()?.toLocaleString('es-MX'));
  }, [userProfile, setActionResult]);

  const runSingle = useCallback(async (actionId) => {
    setResults((prev) => ({ ...prev, [actionId]: { status: 'running', message: '', testedAt: null } }));
    const result = await runAction(actionId, userProfile?.company_id);
    setActionResult(actionId, result);
  }, [userProfile, setActionResult]);

  // Counters
  const allActions = MODULES?.flatMap((m) => m?.actions);
  const tested = allActions?.filter((a) => results?.[a?.id]?.status !== 'pending' && results?.[a?.id]?.status !== 'running');
  const okCount = tested?.filter((a) => results?.[a?.id]?.status === 'ok')?.length;
  const failCount = tested?.filter((a) => results?.[a?.id]?.status !== 'ok')?.length;

  const copyReport = useCallback(() => {
    const lines = [`=== QA Acciones Críticas — ${reportTime ?? new Date()?.toLocaleString('es-MX')} ===\n`];
    MODULES?.forEach((mod) => {
      lines?.push(`\n[${mod?.name}]`);
      mod?.actions?.forEach((action) => {
        const r = results?.[action?.id];
        const statusLabel = STATUS_CONFIG?.[r?.status]?.label ?? r?.status ?? '—';
        lines?.push(`  • ${action?.label}: ${statusLabel}${r?.message ? ` — ${r?.message}` : ''}${r?.testedAt ? ` (${r?.testedAt})` : ''}`);
      });
    });
    lines?.push(`\nResumen: ${okCount} OK / ${failCount} con problemas`);
    navigator.clipboard?.writeText(lines?.join('\n'))?.then(() => alert('Reporte copiado al portapapeles'));
  }, [results, okCount, failCount, reportTime]);

  const role = userProfile?.role;
  const allowed = role === 'admin' || role === 'direccion' || role === 'direction' || import.meta.env?.DEV;

  if (!allowed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8 max-w-md">
          <div className="text-5xl mb-4">🔒</div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Acceso restringido</h2>
          <p className="text-gray-500 mb-4">Esta pantalla es solo para administradores y dirección.</p>
          <button onClick={() => navigate('/')} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-gray-600 transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold text-gray-900">QA — Acciones Críticas</span>
                <span className="text-xs bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full font-medium">MODO DEBUG</span>
              </div>
              <p className="text-xs text-gray-400 mt-0.5">Valida que las acciones principales de cada módulo respondan correctamente</p>
            </div>
          </div>

          {/* Counters */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 bg-green-50 border border-green-200 rounded-lg px-3 py-1.5">
              <span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>
              <span className="text-sm font-semibold text-green-700">{okCount} OK</span>
            </div>
            <div className="flex items-center gap-1.5 bg-red-50 border border-red-200 rounded-lg px-3 py-1.5">
              <span className="w-2 h-2 rounded-full bg-red-500 inline-block"></span>
              <span className="text-sm font-semibold text-red-700">{failCount} con problemas</span>
            </div>
            <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5">
              <span className="text-sm text-gray-500">{tested?.length}/{allActions?.length} probadas</span>
            </div>
          </div>
        </div>
      </div>
      {/* Toolbar */}
      <div className="bg-white border-b border-gray-100 px-6 py-3">
        <div className="max-w-6xl mx-auto flex items-center gap-3 flex-wrap">
          <button
            onClick={runAll}
            disabled={running}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            {running ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                Probando...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                Probar acciones críticas
              </>
            )}
          </button>
          <button
            onClick={copyReport}
            className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>
            Copiar reporte
          </button>
          <button
            onClick={() => { setResults(buildInitial()); setReportTime(null); }}
            className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 014.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
            Limpiar
          </button>
          {/* ── Solo admin login test button ── */}
          <button
            onClick={runAdminLoginSolo}
            disabled={adminLoginRunning || running}
            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors ml-auto"
          >
            {adminLoginRunning ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
                Diagnosticando login admin...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>
                Probar solo login admin
              </>
            )}
          </button>
          {/* ── Auth Health Check button ── */}
          <button
            onClick={runHealthCheck}
            disabled={healthRunning || running}
            className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-300 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            {healthRunning ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
                Auth Health Check...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>
                Auth Health Check
              </>
            )}
          </button>
          {/* ── Raw Auth Request Test button ── */}
          <button
            onClick={runRawAuth}
            disabled={rawAuthRunning || running}
            className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            {rawAuthRunning ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
                Raw Auth Test...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
                Raw Auth Request Test
              </>
            )}
          </button>
          {reportTime && (
            <span className="text-xs text-gray-400">Última prueba: {reportTime}</span>
          )}
        </div>
      </div>
      {/* Modules */}
      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">

        {/* ── Session Bootstrap Phase Panel ── */}
        <div className="bg-white rounded-xl border-2 border-indigo-200 overflow-hidden shadow-sm">
          <div className="px-5 py-3 bg-indigo-50 border-b border-indigo-200 flex items-center gap-2">
            <span className="text-lg">🔄</span>
            <h3 className="font-semibold text-indigo-800 text-sm">Session Bootstrap — Última fase alcanzada</h3>
          </div>
          <div className="px-5 py-4">
            <div className="flex flex-wrap gap-2">
              {['IDLE','SESSION_FOUND','PROFILE_LOAD_START','PROFILE_LOAD_OK','PROFILE_LOAD_ERROR','GUARD_PASS','GUARD_TIMEOUT']?.map((phase) => {
                const isActive = lastBootstrapPhase === phase;
                const isError = phase?.includes('ERROR') || phase?.includes('TIMEOUT');
                const isOk = phase === 'PROFILE_LOAD_OK' || phase === 'GUARD_PASS';
                return (
                  <span key={phase} className={`text-xs font-mono px-3 py-1.5 rounded-full border font-semibold transition-all ${
                    isActive
                      ? isError ? 'bg-red-100 text-red-700 border-red-300 ring-2 ring-red-300' : isOk ?'bg-green-100 text-green-700 border-green-300 ring-2 ring-green-300' :'bg-indigo-100 text-indigo-700 border-indigo-300 ring-2 ring-indigo-300' :'bg-gray-50 text-gray-400 border-gray-200'
                  }`}>
                    {isActive ? '▶ ' : ''}{phase}
                  </span>
                );
              })}
            </div>
            <p className="text-xs text-gray-400 mt-3">
              Fase actual: <span className="font-mono font-semibold text-gray-700">{lastBootstrapPhase || 'IDLE'}</span>
              {' '}— actualizado en tiempo real desde AuthContext
            </p>
          </div>
        </div>

        {/* ── Raw Auth Request Test Panel ── */}
        {(rawAuthRunning || rawAuthResult || rawAuthPhases?.length > 0) && (
          <div className="bg-white rounded-xl border-2 border-orange-200 overflow-hidden shadow-sm">
            <div className="px-5 py-3 bg-orange-50 border-b border-orange-200 flex items-center gap-2">
              <span className="text-lg">⚡</span>
              <h3 className="font-semibold text-orange-800 text-sm">Raw Auth Request Test</h3>
              <span className="text-xs text-orange-500 ml-1">— solo signInWithPassword, sin wrappers</span>
              {rawAuthRunning && (
                <span className="ml-2 text-xs bg-orange-100 text-orange-600 border border-orange-200 px-2 py-0.5 rounded-full animate-pulse">Ejecutando (máx {RAW_AUTH_TIMEOUT_MS / 1000}s)...</span>
              )}
              {rawAuthResult && !rawAuthRunning && (
                <span className={`ml-2 text-xs px-2 py-0.5 rounded-full border font-medium ${
                  rawAuthResult?.status === 'ok' ? 'bg-green-50 text-green-700 border-green-200'
                    : rawAuthResult?.status === 'timeout'? 'bg-yellow-50 text-yellow-700 border-yellow-200' :'bg-red-50 text-red-700 border-red-200'
                }`}>
                  {rawAuthResult?.status === 'ok' ? '✓ RESPONSE_OK' : rawAuthResult?.status === 'timeout' ? '⚠ RESPONSE_TIMEOUT' : '✗ RESPONSE_ERROR'}
                </span>
              )}
              <button
                onClick={() => { setRawAuthPhases([]); setRawAuthResult(null); }}
                className="ml-auto text-orange-400 hover:text-orange-600 text-xs"
              >
                Cerrar
              </button>
            </div>

            {/* Phase list */}
            {rawAuthPhases?.length > 0 && (
              <div className="px-5 py-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">Fases</p>
                <div className="space-y-1.5">
                  {rawAuthPhases?.map((p, i) => {
                    const isError = p?.phase === 'RESPONSE_ERROR';
                    const isTimeout = p?.phase === 'RESPONSE_TIMEOUT';
                    const isOk = p?.phase === 'RESPONSE_OK';
                    const isSent = p?.phase === 'REQUEST_SENT';
                    return (
                      <div key={i} className={`flex items-center gap-3 text-sm rounded-lg px-3 py-1.5 ${
                        isOk ? 'bg-green-50 border border-green-100' : isError ?'bg-red-50 border border-red-200' : isTimeout ?'bg-yellow-50 border border-yellow-200' :'bg-blue-50 border border-blue-100'
                      }`}>
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          isOk ? 'bg-green-500' : isError ? 'bg-red-500' : isTimeout ? 'bg-yellow-500' : 'bg-blue-400'
                        }`} />
                        <span className="font-mono text-xs font-semibold text-gray-800 w-44 flex-shrink-0">{p?.phase}</span>
                        {p?.detail && <span className="text-xs text-gray-600 truncate">{p?.detail}</span>}
                        {p?.time && <span className="ml-auto text-xs text-gray-400 flex-shrink-0">{p?.time}</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Result */}
            {rawAuthResult && (
              <div className={`px-5 py-3 border-t ${
                rawAuthResult?.status === 'ok' ? 'bg-green-50 border-green-200'
                  : rawAuthResult?.status === 'timeout'? 'bg-yellow-50 border-yellow-200' :'bg-red-50 border-red-200'
              }`}>
                <p className="text-xs font-semibold text-gray-700 mb-0.5">Resultado</p>
                <p className={`text-sm font-medium ${
                  rawAuthResult?.status === 'ok' ? 'text-green-700' : rawAuthResult?.status === 'timeout' ? 'text-yellow-700' : 'text-red-700'
                }`}>{rawAuthResult?.message}</p>
                <p className="text-xs text-gray-400 mt-1">Última fase: <span className="font-mono font-semibold">{rawAuthResult?.lastPhase}</span></p>
              </div>
            )}
          </div>
        )}

        {/* ── Admin Login Diagnostic Panel ── */}
        {(adminLoginRunning || adminLoginResult || adminLoginPhases?.length > 0) && (
          <div className="bg-white rounded-xl border-2 border-purple-200 overflow-hidden shadow-sm">
            <div className="px-5 py-3 bg-purple-50 border-b border-purple-200 flex items-center gap-2">
              <span className="text-lg">🔐</span>
              <h3 className="font-semibold text-purple-800 text-sm">Diagnóstico: Login Admin</h3>
              {adminLoginRunning && (
                <span className="ml-2 text-xs bg-purple-100 text-purple-600 border border-purple-200 px-2 py-0.5 rounded-full animate-pulse">Ejecutando (máx 15s)...</span>
              )}
              {adminLoginResult && !adminLoginRunning && (
                <span className={`ml-2 text-xs px-2 py-0.5 rounded-full border font-medium ${
                  adminLoginResult?.status === 'ok' ?'bg-green-50 text-green-700 border-green-200'
                    : adminLoginResult?.status === 'loading' ?'bg-yellow-50 text-yellow-700 border-yellow-200' :'bg-red-50 text-red-700 border-red-200'
                }`}>
                  {adminLoginResult?.status === 'ok' ? '✓ PASÓ' : adminLoginResult?.status === 'loading' ? '⚠ TIMEOUT' : '✗ FALLÓ'}
                </span>
              )}
              <button
                onClick={() => { setAdminLoginPhases([]); setAdminLoginResult(null); }}
                className="ml-auto text-purple-400 hover:text-purple-600 text-xs"
              >
                Cerrar
              </button>
            </div>

            {/* Phase timeline */}
            {adminLoginPhases?.length > 0 && (
              <div className="px-5 py-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">Fases registradas</p>
                <div className="space-y-1.5">
                  {ADMIN_LOGIN_PHASES?.map((phase) => {
                    const reached = adminLoginPhases?.find((p) => p?.phase === phase);
                    const isLast = adminLoginResult?.lastPhase === phase;
                    return (
                      <div key={phase} className={`flex items-center gap-3 text-sm rounded-lg px-3 py-1.5 ${
                        reached
                          ? isLast && adminLoginResult?.status !== 'ok' ?'bg-red-50 border border-red-200'
                            : phase === 'TIMEOUT' ?'bg-yellow-50 border border-yellow-200' :'bg-green-50 border border-green-100'
                          : adminLoginRunning
                          ? 'bg-gray-50 border border-gray-100 opacity-40' :'bg-gray-50 border border-gray-100 opacity-30'
                      }`}>
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          reached
                            ? phase === 'TIMEOUT' || (isLast && adminLoginResult?.status !== 'ok')
                              ? 'bg-red-500' :'bg-green-500' :'bg-gray-300'
                        }`} />
                        <span className={`font-mono text-xs font-semibold w-44 flex-shrink-0 ${
                          reached ? 'text-gray-800' : 'text-gray-400'
                        }`}>{phase}</span>
                        {reached?.detail && (
                          <span className="text-xs text-gray-500 truncate">{reached?.detail}</span>
                        )}
                        {reached?.time && (
                          <span className="ml-auto text-xs text-gray-400 flex-shrink-0">{reached?.time}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Final result */}
            {adminLoginResult && (
              <div className={`px-5 py-3 border-t ${
                adminLoginResult?.status === 'ok' ?'bg-green-50 border-green-200'
                  : adminLoginResult?.status === 'loading' ?'bg-yellow-50 border-yellow-200' :'bg-red-50 border-red-200'
              }`}>
                <p className="text-xs font-semibold text-gray-700 mb-0.5">Resultado final</p>
                <p className={`text-sm font-medium ${
                  adminLoginResult?.status === 'ok' ? 'text-green-700' : adminLoginResult?.status === 'loading' ? 'text-yellow-700' : 'text-red-700'
                }`}>{adminLoginResult?.message}</p>
                <p className="text-xs text-gray-400 mt-1">Última fase alcanzada: <span className="font-mono font-semibold">{adminLoginResult?.lastPhase}</span></p>
              </div>
            )}
          </div>
        )}

        {/* ── Auth Health Check Panel ── */}
        {(healthRunning || healthResult || healthPhases?.length > 0) && (
          <div className="bg-white rounded-xl border-2 border-teal-200 overflow-hidden shadow-sm">
            <div className="px-5 py-3 bg-teal-50 border-b border-teal-200 flex items-center gap-2">
              <span className="text-lg">🩺</span>
              <h3 className="font-semibold text-teal-800 text-sm">Auth Health Check</h3>
              {healthRunning && (
                <span className="ml-2 text-xs bg-teal-100 text-teal-600 border border-teal-200 px-2 py-0.5 rounded-full animate-pulse">Ejecutando...</span>
              )}
              {healthResult && !healthRunning && (
                <span className={`ml-2 text-xs px-2 py-0.5 rounded-full border font-medium ${
                  healthResult?.status === 'ok' ? 'bg-green-50 text-green-700 border-green-200' :'bg-red-50 text-red-700 border-red-200'
                }`}>
                  {healthResult?.status === 'ok' ? '✓ SANO' : '✗ PROBLEMA'}
                </span>
              )}
              <button
                onClick={() => { setHealthPhases([]); setHealthResult(null); }}
                className="ml-auto text-teal-400 hover:text-teal-600 text-xs"
              >
                Cerrar
              </button>
            </div>

            {/* Phase list */}
            {healthPhases?.length > 0 && (
              <div className="px-5 py-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">Fases</p>
                <div className="space-y-1.5">
                  {healthPhases?.map((p, i) => (
                    <div key={i} className={`flex items-center gap-3 text-sm rounded-lg px-3 py-1.5 ${
                      p?.phase?.includes('ERROR') || p?.phase?.includes('MISSING')
                        ? 'bg-red-50 border border-red-200' :'bg-green-50 border border-green-100'
                    }`}>
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        p?.phase?.includes('ERROR') || p?.phase?.includes('MISSING') ? 'bg-red-500' : 'bg-green-500'
                      }`} />
                      <span className="font-mono text-xs font-semibold text-gray-800 w-52 flex-shrink-0">{p?.phase}</span>
                      {p?.detail && <span className="text-xs text-gray-500 truncate">{p?.detail}</span>}
                      {p?.time && <span className="ml-auto text-xs text-gray-400 flex-shrink-0">{p?.time}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Result */}
            {healthResult && (
              <div className={`px-5 py-3 border-t ${
                healthResult?.status === 'ok' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
              }`}>
                <p className="text-xs font-semibold text-gray-700 mb-0.5">Resultado</p>
                <p className={`text-sm font-medium ${
                  healthResult?.status === 'ok' ? 'text-green-700' : 'text-red-700'
                }`}>{healthResult?.message}</p>
                <p className="text-xs text-gray-400 mt-1">Última fase: <span className="font-mono font-semibold">{healthResult?.lastPhase}</span></p>
              </div>
            )}
          </div>
        )}

        {MODULES?.map((mod) => (
          <div key={mod?.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            {/* Module header */}
            <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
              <span className="text-lg">{mod?.icon}</span>
              <h3 className="font-semibold text-gray-800 text-sm">{mod?.name}</h3>
              <span className="ml-auto text-xs text-gray-400">
                {mod?.actions?.filter((a) => results?.[a?.id]?.status === 'ok')?.length}/{mod?.actions?.length} OK
              </span>
            </div>

            {/* Actions table */}
            <div className="divide-y divide-gray-100">
              {/* Table header */}
              <div className="grid grid-cols-12 gap-3 px-5 py-2 text-xs font-medium text-gray-400 uppercase tracking-wide">
                <div className="col-span-3">Acción</div>
                <div className="col-span-4">Descripción</div>
                <div className="col-span-2">Estado</div>
                <div className="col-span-2">Mensaje</div>
                <div className="col-span-1 text-right">Hora</div>
              </div>

              {mod?.actions?.map((action) => {
                const r = results?.[action?.id] ?? { status: 'pending', message: '', testedAt: null };
                const cfg = STATUS_CONFIG?.[r?.status] ?? STATUS_CONFIG?.pending;
                return (
                  <div key={action?.id} className="grid grid-cols-12 gap-3 px-5 py-3 items-center hover:bg-gray-50/50 transition-colors">
                    {/* Action name */}
                    <div className="col-span-3 flex items-center gap-2">
                      <button
                        onClick={() => runSingle(action?.id)}
                        disabled={running || r?.status === 'running'}
                        title="Probar esta acción"
                        className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 hover:bg-blue-100 hover:text-blue-600 text-gray-400 transition-colors disabled:opacity-40 flex-shrink-0"
                      >
                        {r?.status === 'running' ? (
                          <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
                        ) : (
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"/></svg>
                        )}
                      </button>
                      <span className="text-sm font-medium text-gray-800">{action?.label}</span>
                    </div>
                    {/* Description */}
                    <div className="col-span-4 text-xs text-gray-400 leading-snug">{action?.description}</div>
                    {/* Status badge */}
                    <div className="col-span-2">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full border ${cfg?.color}`}>
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg?.dot}`}></span>
                        {cfg?.label}
                      </span>
                    </div>
                    {/* Message */}
                    <div className="col-span-2 text-xs text-gray-500 truncate" title={r?.message}>{r?.message || '—'}</div>
                    {/* Time */}
                    <div className="col-span-1 text-right text-xs text-gray-400">{r?.testedAt || '—'}</div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Summary footer */}
        {reportTime && (
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <h3 className="font-semibold text-gray-800 mb-3 text-sm">Resumen del reporte</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'Acciones OK', value: okCount, color: 'text-green-700 bg-green-50 border-green-200' },
                { label: 'Con problemas', value: failCount, color: 'text-red-700 bg-red-50 border-red-200' },
                { label: 'Total probadas', value: tested?.length, color: 'text-blue-700 bg-blue-50 border-blue-200' },
                { label: 'Total acciones', value: allActions?.length, color: 'text-gray-700 bg-gray-50 border-gray-200' },
              ]?.map((item) => (
                <div key={item?.label} className={`rounded-lg border p-3 text-center ${item?.color}`}>
                  <div className="text-2xl font-bold">{item?.value}</div>
                  <div className="text-xs mt-0.5 opacity-80">{item?.label}</div>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-3">Prueba ejecutada: {reportTime}</p>
          </div>
        )}

        <div className="text-center text-xs text-gray-300 pb-4">
          /qa/actions — Solo lectura. No se modifican datos reales.
        </div>
      </div>
    </div>
  );
}
